import { Check, ShieldAlert, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { EmptyState, LoadingState, MetricStrip, PageHeader, StatusBadge, TaskTypeIcon, formatDate, typeLabels } from "../components";
import { useAppData } from "../context";

export function Approvals() {
  const { data, refresh } = useAppData();
  const [selectedId, setSelectedId] = useState("");
  const [reason, setReason] = useState("已核对账号、站点、变化范围与拦截明细");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const tasks = data?.tasks.filter((task) => task.status === "WAITING_APPROVAL") ?? [];
  useEffect(() => { if (!selectedId && tasks[0]) setSelectedId(tasks[0].id); }, [selectedId, tasks]);
  if (!data) return <LoadingState />;
  const selected = tasks.find((task) => task.id === selectedId) ?? tasks[0];
  const canApprove = data.writeEnabled;

  async function decide(decision: "approve" | "reject") {
    if (!selected) return;
    setBusy(true); setMessage("");
    try {
      if (decision === "approve") await api.approve(selected.id, reason);
      else await api.reject(selected.id, reason);
      await refresh();
      setSelectedId("");
      setMessage(decision === "approve" ? "任务已批准并进入队列。" : "任务已驳回。" );
    } catch (error) { setMessage(error instanceof Error ? error.message : "审批失败。" ); }
    finally { setBusy(false); }
  }

  return <>
    <PageHeader eyebrow="APPROVAL / RISK" title="高风险审批" />
    {message ? <div className="inline-message" role="status">{message}</div> : null}
    {!tasks.length ? <EmptyState title="当前没有待审批任务" /> : <div className="approval-layout">
      <section className="workspace-band approval-list">
        <div className="section-heading"><div><h2>待审批</h2><span>{tasks.length} 个任务</span></div></div>
        {tasks.map((task) => <button key={task.id} className={selected?.id === task.id ? "active" : ""} onClick={() => setSelectedId(task.id)}><TaskTypeIcon type={task.taskType} /><div><strong className="mono">{task.taskNo}</strong><span>{typeLabels[task.taskType]} · {task.creator}</span><small>{formatDate(task.createdAt)}</small></div><StatusBadge status={task.status} /></button>)}
      </section>
      {selected ? <section className="workspace-band approval-review">
        <div className="approval-title"><div><span>{typeLabels[selected.taskType]}</span><h2>{selected.taskNo}</h2><small>{selected.note || "--"}</small></div><button className="text-link" onClick={() => navigate(`/tasks/${selected.id}`)}>查看全部明细</button></div>
        <MetricStrip items={[{ label: "导入", value: selected.summary.total }, { label: "可执行", value: selected.summary.executable, tone: "positive" }, { label: "已拦截", value: selected.summary.blocked, tone: "warning" }, { label: "站点", value: selected.siteCount }]} />
        <div className="approval-risks"><ShieldAlert size={20} /><div><strong>触发原因</strong>{selected.riskReasons.map((risk) => <span key={risk}>{risk}</span>)}</div></div>
        <div className="field-group"><label htmlFor="approval-reason">审批意见</label><textarea id="approval-reason" rows={4} value={reason} onChange={(event) => setReason(event.target.value)} /></div>
        {!canApprove ? <div className="permission-note">当前为生产只读模式，审批执行已锁定。</div> : null}
        <div className="approval-actions"><button className="button danger-secondary" disabled={!canApprove || busy || !reason.trim()} onClick={() => void decide("reject")}><X size={17} />驳回</button><button className="button primary" disabled={!canApprove || busy || !reason.trim()} onClick={() => void decide("approve")}><Check size={17} />批准并执行</button></div>
      </section> : null}
    </div>}
  </>;
}
